import { logError, logInfo } from '../utilities/logging';
import { CartItem } from './addToCart';

const isShopifyCdnUrl = (value: unknown) => typeof value === 'string' && value.includes('https://cdn.shopify.com/s/files/');
const isUvImagePropertyKey = (key: string) => key.startsWith('UV') && key.toLowerCase().includes('image');
const isUvInsertChoiceId = (choiceId: unknown) =>
  typeof choiceId === 'string' && /^[a-zA-Z0-9_-]+:\d+$/.test(choiceId);

export const createImageSwitchPayload = ({
  item
}: {
  item: CartItem;
}): { newPayload: { id: string; properties: Record<string, unknown> } | false } => {
  const { properties } = item;

  // Skip items that don't have properties
  if (!properties || typeof properties !== 'object') {
    console.log('Skipping item without properties:', item);
    return { newPayload: false };
  }

  // Parse _personalisation_base_attributes first to prepare for IMAGE_UPLOAD URLs
  const rawBaseAttributes = properties['_personalisation_base_attributes'];
  let baseAttributes: Record<string, unknown> = {};
  if (typeof rawBaseAttributes === 'string') {
    try {
      const parsedBaseAttributes = JSON.parse(rawBaseAttributes);
      if (parsedBaseAttributes && typeof parsedBaseAttributes === 'object') {
        baseAttributes = parsedBaseAttributes;
      }
    } catch (error) {
      logError({
        label: 'addImageUrls',
        error,
        message: 'Failed to parse _personalisation_base_attributes JSON string',
        extraData: { baseAttributesString: rawBaseAttributes }
      });
    }
  }
  // UV insert uploads are stored inside `_personalisation_choices`, so we parse that
  // structure up front before redistributing any Shopify CDN image URLs.
  const rawPersonalisationChoices: unknown = properties['_personalisation_choices'];
  let personalisationChoices: unknown = rawPersonalisationChoices;
  const choicesWereStringified = typeof rawPersonalisationChoices === 'string';
  if (choicesWereStringified) {
    try {
      personalisationChoices = JSON.parse(rawPersonalisationChoices);
    } catch (error) {
      logError({
        label: 'addImageUrls',
        error,
        message: 'Failed to parse _personalisation_choices JSON string',
        extraData: { personalisationChoicesString: personalisationChoices }
      });
      personalisationChoices = [];
    }
  }
  const parsedChoices: Array<Record<string, unknown>> = Array.isArray(personalisationChoices)
    ? [...(personalisationChoices as Array<Record<string, unknown>>)]
    : [];

  const newProps = Object.entries({ ...properties }).reduce<Record<string, unknown>>((acc, [key, value]) => {
    // Leave the source containers in place while we rebuild them with any moved image URLs.
    if (key === '_personalisation_base_attributes' || key === '_personalisation_choices') {
      acc[key] = value;
      return acc;
    }
    if (isShopifyCdnUrl(value)) {
      if (isUvImagePropertyKey(key)) {
        // UV insert image uploads need to stay attached to the matching choice payload so
        // the insert keeps its own asset instead of being flattened into base attributes.
        const matchingChoiceIndex = parsedChoices.findIndex((choice) => {
          if (!isUvInsertChoiceId(choice?.c)) return false;
          const choiceProps = choice?.properties;
          return typeof choiceProps === 'object' && choiceProps !== null && key in choiceProps;
        });
        if (matchingChoiceIndex >= 0) {
          const matchingChoice = parsedChoices[matchingChoiceIndex];
          parsedChoices[matchingChoiceIndex] = {
            ...matchingChoice,
            properties: {
              ...(matchingChoice?.properties && typeof matchingChoice.properties === 'object'
                ? matchingChoice.properties
                : {}),
              [key]: value
            }
          };
          return acc;
        }
      }
      // Check if this CDN URL belongs to a photo variant IMAGE_UPLOAD choice.
      // Photo variant choices have c in "{slotId}:{variantId}" format, a `properties` object
      // keyed by image LIP names, and a top-level `lip` field naming the primary LIP key.
      // `lip` is removed from the choice once v is populated so it never appears in the final cart.
      const photoVariantChoiceIndex = parsedChoices.findIndex((choice) => {
        if (!isUvInsertChoiceId(choice?.c)) return false;
        const props = choice?.properties;
        if (typeof props !== 'object' || props === null) return false;
        return key in (props as Record<string, unknown>);
      });
      if (photoVariantChoiceIndex >= 0) {
        const matchingChoice = parsedChoices[photoVariantChoiceIndex];
        const existingProps = (
          typeof matchingChoice.properties === 'object' && matchingChoice.properties !== null
            ? matchingChoice.properties
            : {}
        ) as Record<string, unknown>;
        const isPrimaryKey = typeof matchingChoice.lip === 'string' && key === matchingChoice.lip;

        if (isPrimaryKey) {
          // Primary LIP key: set v, update properties, and drop the lip sentinel.
          const { lip: _removedLip, ...restChoice } = matchingChoice;
          parsedChoices[photoVariantChoiceIndex] = {
            ...restChoice,
            properties: { ...existingProps, [key]: value },
            v: value
          };
        } else {
          parsedChoices[photoVariantChoiceIndex] = {
            ...matchingChoice,
            properties: { ...existingProps, [key]: value }
          };
        }
        // Keep the CDN URL as a direct flat property so the mini cart template can
        // render it as an image thumbnail. The image is intentionally NOT added to
        // _personalisation_base_attributes because it belongs to the insert SKU,
        // not the base product — but it does need to be visible in the mini cart.
        acc[key] = value;
        return acc;
      }

      // Move non-UV, non-photo-variant uploaded images to base attributes.
      baseAttributes[key] = value;
      return acc;
    }
    acc[key] = value;

    return acc;
  }, {});

  // @TODO: Remove this when we have a better flow for handling visualiser images.
  const baseAttributesEntries = Object.entries(baseAttributes);
  const baseAttributesNoDupes = baseAttributesEntries.reduce((acc, [key, value]) => {
    // Check if there are any matching values for this in baseAttributes with a key that ends up 'Visual' that isn't itself.
    if (baseAttributesEntries.some(([key2, value2]) => key2.endsWith(' Visual') && value2 === value && key2 !== key)) {
      return acc;
    }
    acc[key.replace(' - Visual', ' Visual')] = value;
    return acc;
  }, {});

  // Always add the updated _personalisation_base_attributes
  newProps['_personalisation_base_attributes'] = baseAttributesNoDupes;
  // Preserve the original shape because some cart items store choices as a JSON string
  // and others already provide an array-like structure.
  newProps['_personalisation_choices'] = choicesWereStringified ? JSON.stringify(parsedChoices) : parsedChoices;

  // For cart changes, we need to use the cart item key
  // The key is a unique identifier for the cart item that includes the variant ID and any properties
  if (!item.key) {
    logError({
      label: 'addImageUrls',
      error: new Error('Cart item key not found - cannot update cart item'),
      message: 'Cart item key not found - cannot update cart item',
      extraData: { item },
      throwError: true
    });
  }

  // Only proceed if there are actual changes to make
  const hasPropertyChanges = Object.keys(newProps).length > 0;

  if (!hasPropertyChanges) {
    logInfo({
      label: 'addImageUrls',
      message: 'No changes needed for item',
      extraData: { item }
    });
    return { newPayload: false };
  }

  const newPayload = {
    id: item.key,
    properties: newProps
  };

  // Validate the payload before sending
  if (!newPayload.id) {
    logError({
      label: 'addImageUrls',
      error: new Error(`Invalid cart item ID: ${newPayload.id}`),
      message: `Invalid cart item ID: ${newPayload.id}`,
      extraData: { newPayload },
      throwError: true
    });
  }

  if (!newPayload.properties || typeof newPayload.properties !== 'object') {
    logError({
      label: 'addImageUrls',
      error: new Error('Invalid properties object'),
      message: 'Invalid properties object',
      extraData: { newPayload },
      throwError: true
    });
  }

  return { newPayload };
};
